import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { SupportAttachmentDto } from '@/api/control/missionImpactAPI.schemas';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useLanguage } from '@/contexts/LanguageContext';
import { fillSupportTemplate } from '@/lib/support';

interface SupportImageViewerProps {
  images: SupportAttachmentDto[];
  urls: Record<string, string>;
  index: number | null;
  onIndexChange: (index: number | null) => void;
}

/**
 * Full-size view of a reply's screenshots, with previous and next buttons when there are several.
 * Closed when the index is null.
 */
export function SupportImageViewer({ images, urls, index, onIndexChange }: SupportImageViewerProps) {
  const { t } = useLanguage();
  const image = index !== null ? images[index] : undefined;
  const url = image?.id ? urls[image.id] : undefined;
  const hasMany = images.length > 1;

  return (
    <Dialog open={image !== undefined} onOpenChange={(open) => { if (!open) onIndexChange(null); }}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="truncate">{image?.fileName ?? t.support.detail.imageViewerTitle}</DialogTitle>
          <DialogDescription>
            {hasMany && index !== null
              ? fillSupportTemplate(t.support.detail.imageCount, { index: index + 1, total: images.length })
              : t.support.detail.imageViewerTitle}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          {hasMany && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={t.support.detail.previousImage}
              onClick={() => { if (index !== null) onIndexChange((index - 1 + images.length) % images.length); }}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          )}
          <div className="flex min-h-64 flex-1 items-center justify-center rounded-md bg-muted">
            {url && <img src={url} alt={image?.fileName ?? ''} className="max-h-[70vh] max-w-full object-contain" />}
          </div>
          {hasMany && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={t.support.detail.nextImage}
              onClick={() => { if (index !== null) onIndexChange((index + 1) % images.length); }}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
